import api from './api';
import { toast } from 'react-toastify';

// Lista de endpoints que precisam de validação de plano
const PROTECTED_ENDPOINTS = {
  'POST /companies/job-postings': 'createJobPosting',
  'GET /companies/candidates/details': 'viewCandidateDetails',
  'POST /companies/candidates/export': 'exportCandidates',
  'GET /companies/analytics': 'analytics',
  'POST /companies/team-members': 'addTeamMember',
};

// Cache para evitar múltiplas requisições idênticas
const requestCache = new Map();
const CACHE_DURATION = 30000; // 30 segundos

// Interceptor para requisições
api.interceptors.request.use(
  (config) => {
    // Adicionar timestamp apenas para GETs que podem ser cacheados
    if (config.method === 'get' && !config.params?._skipCache) {
      const cacheKey = `${config.method}_${config.url}_${JSON.stringify(config.params || {})}`;
      const cached = requestCache.get(cacheKey);

      if (cached && (Date.now() - cached.timestamp) < CACHE_DURATION) {
        // Retornar dados do cache
        config._cached = cached.data;
      } else {
        // Adicionar timestamp para evitar cache do browser
        config.params = {
          ...config.params,
          _t: Date.now()
        };
      }
    }

    // Adicionar header para identificar origem da requisição
    config.headers = {
      ...config.headers,
      'X-App-Source': 'ecomlovers-web'
    };

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Interceptor para respostas
api.interceptors.response.use(
  (response) => {
    // Cachear respostas GET se apropriado
    if (response.config.method === 'get' && !response.config.params?._skipCache) {
      const cacheKey = `${response.config.method}_${response.config.url}_${JSON.stringify(response.config.params || {})}`;
      requestCache.set(cacheKey, {
        data: response,
        timestamp: Date.now()
      });
    }

    // Verificar se a resposta indica limitação do plano
    if (response.data?.meta?.planLimitReached) {
      const limitation = response.data.meta.planLimitReached;

      // Usar toast mais suave para não incomodar o usuário
      if (!sessionStorage.getItem('planLimitToastShown')) {
        toast.warning(`Limite do plano: ${limitation.message}`);
        sessionStorage.setItem('planLimitToastShown', 'true');

        // Limpar flag após 5 minutos
        setTimeout(() => {
          sessionStorage.removeItem('planLimitToastShown');
        }, 5 * 60 * 1000);
      }

      // Redirecionar para upgrade apenas se explicitamente solicitado
      if (limitation.requiresUpgrade && limitation.forceRedirect) {
        setTimeout(() => {
          window.location.href = '/upgrade-plan';
        }, 2000);
      }
    }

    return response;
  },
  (error) => {
    // Tratar erros específicos de planos
    if (error.response?.status === 403) {
      const errorData = error.response.data;

      // Verificar se é erro de limitação de plano
      if (errorData?.error?.code === 'PLAN_LIMIT_EXCEEDED') {
        // Evitar spam de toasts
        const errorKey = `planLimit_${errorData.error.feature || 'general'}`;
        if (!sessionStorage.getItem(errorKey)) {
          toast.error(errorData.error.message || 'Limite do plano atingido');
          sessionStorage.setItem(errorKey, 'true');

          // Limpar flag após 2 minutos
          setTimeout(() => {
            sessionStorage.removeItem(errorKey);
          }, 2 * 60 * 1000);
        }

        // Não rejeitar a promise para evitar quebrar a aplicação
        return Promise.resolve({
          data: {
            error: true,
            planLimitExceeded: true,
            message: errorData.error.message,
            feature: errorData.error.feature
          }
        });
      }

      // Verificar se é erro de plano expirado
      if (errorData?.error?.code === 'PLAN_EXPIRED') {
        if (!sessionStorage.getItem('planExpiredToastShown')) {
          toast.error('Seu plano expirou. Renove para continuar usando a plataforma.');
          sessionStorage.setItem('planExpiredToastShown', 'true');
        }

        setTimeout(() => {
          localStorage.removeItem('@ecomlovers:user');
          localStorage.removeItem('@ecomlovers:token');
          window.location.href = '/login';
        }, 3000);

        return Promise.reject(error);
      }
    }

    // Verificar se o token expirou ou é inválido
    if (error.response?.status === 401) {
      // Para qualquer erro 401 (token inválido, não autorizado, etc.)
      if (!sessionStorage.getItem('tokenExpiredToastShown')) {
        toast.error('Sua sessão expirou. Faça login novamente.');
        sessionStorage.setItem('tokenExpiredToastShown', 'true');
      }

      // Limpar dados do localStorage
      localStorage.removeItem('@ecomlovers:user');
      localStorage.removeItem('@ecomlovers:token');

      // Limpar header de autorização
      delete api.defaults.headers.Authorization;

      // Redirecionar para login
      setTimeout(() => {
        window.location.href = '/';
      }, 2000);

      return Promise.reject(error);
    }

    return Promise.reject(error);
  }
);

// Função helper para verificar se uma requisição é protegida
export const isProtectedEndpoint = (method, url) => {
  const key = `${method.toUpperCase()} ${url}`;
  return PROTECTED_ENDPOINTS[key];
};

// Função otimizada para fazer requisições com validação de plano
export const makeProtectedRequest = async (method, url, data = null, config = {}) => {
  try {
    // Verificar cache primeiro para GETs
    if (method.toLowerCase() === 'get' && !config._skipCache) {
      const cacheKey = `${method}_${url}_${JSON.stringify(data || {})}`;
      const cached = requestCache.get(cacheKey);

      if (cached && (Date.now() - cached.timestamp) < CACHE_DURATION) {
        return {
          success: true,
          data: cached.data.data,
          fromCache: true
        };
      }
    }

    const response = await api({
      method,
      url,
      data,
      ...config
    });

    // Verificar se a resposta indica limitação
    if (response.data?.planLimitExceeded) {
      return {
        success: false,
        planLimitExceeded: true,
        message: response.data.message,
        feature: response.data.feature
      };
    }

    return {
      success: true,
      data: response.data
    };
  } catch (error) {
    // Se o erro foi tratado pelo interceptor, não relançar
    if (error.response?.data?.planLimitExceeded) {
      return {
        success: false,
        planLimitExceeded: true,
        message: error.response.data.message,
        feature: error.response.data.feature
      };
    }

    throw error;
  }
};

// Função para limpar cache quando necessário
export const clearRequestCache = () => {
  requestCache.clear();
};

// Função para fazer requisições em batch (reduzir múltiplas chamadas)
export const makeBatchRequest = async (requests) => {
  try {
    const response = await api.post('/batch', {
      requests: requests.map(req => ({
        method: req.method,
        url: req.url,
        data: req.data
      }))
    });

    return response.data.map((result, index) => ({
      success: !result.error,
      data: result.data,
      error: result.error,
      originalRequest: requests[index]
    }));
  } catch (error) {
    // Fallback para requisições individuais
    const results = [];
    for (const request of requests) {
      try {
        const result = await makeProtectedRequest(request.method, request.url, request.data);
        results.push(result);
      } catch (err) {
        results.push({
          success: false,
          error: err.message,
          originalRequest: request
        });
      }
    }
    return results;
  }
};

export default api;
